import { api } from "@/convex/_generated/api";
import type { ServiceOfferVariant } from "@/convex/crm/serviceCatalogShared";
import { useAuth } from "@/hooks/use-auth";
import { localizedPath } from "@/lib/routes";
import {
  ServiceCommerceContext,
  type ServiceComboItem,
  type ServiceCommerceContextValue,
} from "@/lib/service-commerce-context";
import { useAction, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const STORAGE_KEY = "enkey-service-combo-v1";

function validStoredItem(value: unknown): value is ServiceComboItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ServiceComboItem>;
  return (
    typeof item.serviceKey === "string" &&
    ["classic", "ai", "pro"].includes(item.offerVariant ?? "") &&
    typeof item.label === "string" &&
    (item.setupAmount === undefined || typeof item.setupAmount === "number") &&
    (item.monthlyAmount === undefined || typeof item.monthlyAmount === "number") &&
    (item.recurringMonths === undefined || (Number.isInteger(item.recurringMonths) && item.recurringMonths! >= 1 && item.recurringMonths! <= 36))
  );
}

export function ServiceCommerceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation("services");
  const { user, signinRedirect } = useAuth();
  const [items, setItems] = useState<ServiceComboItem[]>([]);
  const [open, setOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const createBundleCheckout = useAction(
    api.payments.stripe.createServiceBundleCheckout,
  );
  const ensureRewards = useMutation(api.customerRewards.ensureMyAccount);
  const rewards = useQuery(api.customerRewards.myBalance, {});

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
      if (Array.isArray(parsed)) setItems(parsed.filter(validStoredItem).map((item:ServiceComboItem)=>item.serviceKey==="marketing" && item.monthlyAmount!==undefined && item.recurringMonths===undefined ? {...item,monthlyAmount:Math.round(item.monthlyAmount*100)*3/100,recurringMonths:3}:item));
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (user && rewards === null) void ensureRewards({});
  }, [ensureRewards, rewards, user]);

  const addItem = useCallback(
    (item: ServiceComboItem) => {
      setItems((current) => {
        const duplicate = current.some(
          (candidate) =>
            candidate.serviceKey === item.serviceKey &&
            candidate.offerVariant === item.offerVariant,
        );
        return duplicate ? current.map(candidate=>candidate.serviceKey===item.serviceKey && candidate.offerVariant===item.offerVariant ? item : candidate) : [...current, item];
      });
      setOpen(true);
      toast.success(t("combo.added"));
    },
    [t],
  );

  const removeItem = useCallback(
    (serviceKey: string, offerVariant: ServiceOfferVariant) => {
      setItems((current) =>
        current.filter(
          (item) =>
            item.serviceKey !== serviceKey ||
            item.offerVariant !== offerVariant,
        ),
      );
    },
    [],
  );
  const clear = useCallback(() => setItems([]), []);

  const checkout = useCallback(
    async (rewardMicrochips: number, locale: string, agreementPreviewSha256: string, buyer: import("@/pages/services/_components/agreement-review-dialog").BuyerInput, selection: ServiceComboItem[] = items) => {
      if (selection.length < 1) {
        toast.info(t("combo.minimum"));
        return;
      }
      if (!user) {
        toast.info(t("buy.signin_title"), {
          description: t("buy.signin_desc"),
        });
        await signinRedirect(window.location.href);
        return;
      }
      setCheckoutLoading(true);
      try {
        const origin = window.location.origin;
        const base = localizedPath("services", locale);
        const result = await createBundleCheckout({
          items: selection.map(({ serviceKey, offerVariant, voucherCode }) => ({
            serviceKey,
            offerVariant,
            voucherCode: voucherCode?.trim() || undefined,
          })),
          rewardMicrochips:
            rewardMicrochips > 0 ? Math.floor(rewardMicrochips) : undefined,
          locale,
          agreementPreviewSha256,
          buyer,
          successUrl: `${origin}${base}?purchase=success&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${origin}${base}?purchase=cancel`,
        });
        window.location.href = result.url;
      } catch (error) {
        const message =
          error instanceof ConvexError
            ? (error.data as { message?: string }).message
            : error instanceof Error
              ? error.message
              : undefined;
        toast.error(t("buy.error_title"), {
          description: message || t("buy.error_generic"),
        });
        setCheckoutLoading(false);
      }
    },
    [createBundleCheckout, items, signinRedirect, t, user],
  );

  const value = useMemo<ServiceCommerceContextValue>(
    () => ({
      items,
      addItem,
      removeItem,
      clear,
      open,
      setOpen,
      balanceMicrochips: rewards?.balanceMicrochips ?? 0,
      checkout,
      checkoutLoading,
    }),
    [
      addItem,
      checkout,
      checkoutLoading,
      clear,
      items,
      open,
      removeItem,
      rewards?.balanceMicrochips,
    ],
  );

  return (
    <ServiceCommerceContext.Provider value={value}>
      {children}
    </ServiceCommerceContext.Provider>
  );
}
